import { $, bindClick } from "../lib/dom.js";
import {
LIFECYCLE_LABELS, STEPS, OVERVIEW_HINTS, CHECKLIST_HINTS,
labelTenantKind, labelEnv, labelStatus, checklistLabel,
} from "../lib/labels.js";
import { escHtml, bindHintTooltips, statCard } from "../lib/hints.js";
import { pretty } from "../lib/util.js";
import { state } from "../state.js";
import {
LS, persistAuth, authMode, portalToken, setAuthSession, api,
} from "../api.js";
import { buildDistHandoffUrl } from "../auth-gate.js";
import { merchantHasDistGated } from "../nav.js";
import { ensureActorsEmbed } from "./actors-embed.js";
import { initMallCatalogHandlers, loadMallCatalog } from "./mall-catalog.js";
import { initBrandHandlers, loadBrandPanel } from "./brand.js";
import { initBminiappHandlers, loadBminiappPanel } from "./bminiapp.js";
import { initOwnStoresHandlers, loadOwnStoresPanel } from "./own-stores.js";
import { initTeamHandlers, loadTeamPanel } from "./team.js";
import { initChannelReviewsHandlers, loadChannelReviewsPanel } from "./channel-reviews.js";
import { initMallOrdersHandlers, loadMallOrdersPanel } from "./mall-orders.js";
import { initMallReconcileHandlers, loadMallReconcilePanel } from "./mall-reconcile.js";
import { initCategoriesHandlers, loadCategoriesPanel } from "./categories.js";
import { initMallStoreHandlers, loadMallStorePanel } from "./mall-store.js";
import { initDistOpenHandlers, renderDistOpenButton } from "./dist-open.js";
import { renderCapabilityOpenPanel } from "./capabilities.js";

export { renderDistOpenButton };

export function showBanner(text, ok) {
const el = $("key-banner");
if (!el) return;
el.textContent = text;
el.classList.toggle("hidden", !text);
el.classList.toggle("ok", !!ok);
}

function lifecycleBadgeClass(lc) {
  if (lc === "live") return "live";
  if (lc === "suspended" || lc === "rejected") return "bad";
  if (lc === "verifying" || lc === "pending_review") return "warn";
  return "";
}

export function renderBadges(me) {
  // 「商家基本资料」（原能力配置页的店铺资料）已迁到概览页顶部，可见性跟着 tenant_kind
  const profile = $("overview-merchant-profile");
  if (profile) {
    profile.classList.toggle(
      "hidden",
      ((me && me.tenant_kind) || "") !== "platform_mall"
    );
  }
  const box = $("overview-badges");
  if (!box) return;
  if (!me) {
    box.innerHTML = statCard("连接状态", "未连接", "warn", "尚未拉取到商家账号信息，请检查登录或刷新。");
    bindHintTooltips(box);
    return;
  }
  const lc = me.lifecycle_status || me.status || "—";
  const tk = me.tenant_kind || "—";
  const env = me.env || localStorage.getItem(LS.env) || "sandbox";
  box.innerHTML = [
    statCard("生命周期", LIFECYCLE_LABELS[lc] || lc, lifecycleBadgeClass(lc) || "neutral", OVERVIEW_HINTS.lifecycle),
    statCard("接入类型", labelTenantKind(tk), "neutral", OVERVIEW_HINTS.tenant_kind),
    statCard("API 环境", labelEnv(env), env === "live" ? "live" : "neutral", OVERVIEW_HINTS.env),
    statCard("审核状态", labelStatus(me.status), lifecycleBadgeClass(lc) || "neutral", OVERVIEW_HINTS.status),
  ].join("");
  bindHintTooltips(box);

  const line = $("console-shop-line");
  if (line) line.textContent = `${me.shop_name || "—"} · ${me.merchant_id || ""}`;
  const meta = $("console-session-meta");
  if (meta) {
    const labels = window.McAuth
      ? window.McAuth.roleDisplayNames()
      : (me.roles || []).map((r) =>
          typeof r === "object" ? (r.name || r.code || "") : String(r || "")
        ).filter(Boolean);
    const rolesText = labels.length ? labels.join("、") : "未分配角色";
    const modeText = authMode() === "portal" ? "扫码会话" : "接口密钥";
    const tip = `角色：${rolesText}｜登录：${modeText}`;
    meta.setAttribute("data-hint", tip);
    meta.setAttribute("aria-label", tip);
    meta.title = tip;
    meta.classList.remove("hidden");
    delete meta.dataset.hintBound;
  }
  bindHintTooltips(document.querySelector(".mc-console-header-actions"));
}

/**
 * 页头「分销管理平台」按钮（共享 S4 免登 · SHD-05 展示）。
 *
 * 显隐**只由后端**决定（`GET /merchant/dist-portal`，两道门槛都过才 `enabled`：
 * ① 本商家已开通共享分销＝dist 有 ACTIVE HQ 树；② 本人持 `dist` 系统许可）
 * ——前端不做二次推断，避免两处口径漂移。
 * 点击在新标签页打开免登 handoff（`buildDistHandoffUrl`，控制台留在原页）。
 * 点击时**重新取一次本端点**，用服务端按当前会话现签的短票 handoff。
 *
 * 刻意不抛错：按钮是可选的增强入口，探测失败/未获授权一律保持隐藏，不影响控制台。
 */
export async function renderDistPortalButton() {
  const btn = $("btn-dist-portal");
  if (!btn) return;
  btn.classList.add("hidden");
  btn.onclick = null;
  let data = null;
  try {
    const res = await api("GET", "/api/v1/merchant/dist-portal");
    if (res.ok) data = res.data;
  } catch {
    /* 探测失败：保持隐藏 */
  }
  if (!data || !data.enabled || !data.url) return;
  btn.classList.remove("hidden");
  const target = data.url;
  btn.onclick = async () => {
    // 弹窗必须**同步**开，先占位空页，取票后再把地址塞进去（防拦截器误伤）
    const win = window.open("about:blank", "_blank");
    if (!win) {
      showBanner("浏览器拦截了新标签页，请允许弹出窗口后重试", false);
      return;
    }
    win.opener = null; // 断 opener，防目标页反向操作本页
    // **点击当刻**重新取票：服务端按当前会话现签短票（5 分钟），
    // 不用 localStorage 里的 7 天长票——长票会把上一个商家的身份带进分销
    // （2026-10-03 串租户事故：人在梦舒雅、SPU 全是演示旗舰店A）。
    let fresh = null;
    try {
      const res = await api("GET", "/api/v1/merchant/dist-portal");
      if (res.ok) fresh = res.data;
    } catch {
      fresh = null;
    }
    if (!fresh || !fresh.enabled || !fresh.url || !fresh.portal_token) {
      win.close();
      showBanner("登录状态已变化，请重新扫码登录控制台后再进入分销", false);
      return;
    }
    let href = "";
    try {
      href = buildDistHandoffUrl(fresh.url, {
        token: fresh.portal_token,
        merchantId: fresh.merchant_id,
        personId: fresh.person_id,
      });
    } catch (e) {
      // 最典型：接口密钥登录无门户令牌，换不了分销票
      win.close();
      showBanner(String((e && e.message) || "无法跳转分销"), false);
      return;
    }
    win.location.replace(href);
  };
}

export function renderChecklist(data) {
  const box = $("overview-checklist");
  if (!box) return;
  if (!data || !Array.isArray(data.items)) {
    box.innerHTML = '<p class="mc-overview-empty">尚无入驻数据</p>';
    $("checklist-pct").style.width = "0%";
    $("checklist-label").textContent = "尚无入驻清单";
    return;
  }
  const done = data.items.filter((i) => i.done).length;
  const total = data.items.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  $("checklist-pct").style.width = `${pct}%`;
  const readyText = data.ready_to_verify ? "可提交验收" : "尚不可提交";
  $("checklist-label").textContent = `已完成 ${done} / ${total} 项（${pct}%）· ${readyText}`;
  const tk = (state.me && state.me.tenant_kind) || (data && data.tenant_kind) || "";
  box.innerHTML = `<ul class="mc-checklist-grid">${data.items
    .map((it) => {
      const cls = it.done ? "done" : "todo";
      const hint = CHECKLIST_HINTS[it.id] || checklistLabel(it, tk);
      return (
        `<li class="mc-checklist-item ${cls}" data-hint="${escHtml(hint)}">` +
        `<span class="mc-checklist-mark" aria-hidden="true">${it.done ? "✓" : "○"}</span>` +
        `<span class="mc-checklist-text">${escHtml(checklistLabel(it, tk))}</span>` +
        `</li>`
      );
    })
    .join("")}</ul>`;
  bindHintTooltips(box);
}

function renderLifecycleSteps(lc) {
  const box = $("lifecycle-steps");
  let idx = 0;
  if (lc === "pending_review") idx = 0;
  else if (lc === "approved_sandbox") idx = 1;
  else if (lc === "verifying") idx = 2;
  else if (lc === "live") idx = 3;
  else if (lc === "rejected" || lc === "suspended") idx = -1;
  box.innerHTML = STEPS.map((s, i) => {
    let cls = "mcp-step";
    if (idx >= i) cls += " done";
    if (idx === i) cls += " current";
    return `<span class="${cls}">${s.label}</span>`;
  }).join("");
}

/* 入驻面板：平台审核不在商家控制台手工填 Key；仅保留商家自助提交验收 */
export function updateAdminOpsUx() {
  const hint = $("admin-ops-hint");
  if (hint) {
    hint.textContent =
      "平台审核与验收上线由运营账号完成；商家只需提交验收申请。当前请求使用已登录会话（门户令牌或商家 Api-Key）。";
  }
}

export function renderOnboardingStatus(me, ob) {
  const el = $("onboarding-status");
  const rej = $("reject-reason");
  if (!me) {
    el.textContent = "尚未加载商家信息";
    rej.classList.add("hidden");
    return;
  }
  el.innerHTML = `
    merchant_id: <code>${me.merchant_id}</code> ·
    lifecycle: <strong>${LIFECYCLE_LABELS[me.lifecycle_status] || me.lifecycle_status}</strong> ·
    tenant_kind: <code>${me.tenant_kind || "—"}</code>`;
  renderLifecycleSteps(me.lifecycle_status);
  const obData = ob || me.onboarding || {};
  const code = obData.reject_reason_code;
  const text = obData.reject_reason_text;
  if (me.lifecycle_status === "rejected" && (code || text)) {
    rej.textContent = `驳回：${code || ""} ${text || ""}`.trim();
    rej.classList.remove("hidden");
    rej.classList.remove("ok");
  } else {
    rej.classList.add("hidden");
  }
}

export function renderCapabilitiesPanel() {
  const tk = state.me?.tenant_kind || "platform_mall";
  const mall = tk === "platform_mall";
  const plug = tk === "plugin" || tk === "dist_enterprise";
  $("cap-mall").classList.toggle("hidden", !mall);
  $("cap-plugin").classList.toggle("hidden", !plug);
  $("cap-hint").textContent = mall
    ? "平台店：登记货架、维护店铺资料、制作分销分享链接（无需 dist 系统）。"
    : plug
      ? "插件：配置域名白名单与 P-EVT 回调。"
      : "未知 tenant_kind";
  // 「货架登记」块（#mall-catalog-wrap）的说明自己写着「无分销 dist 系统时，可直接在平台登记商品」
  // —— 2026-10-14 主人令：**加门控**。本店已有分销 dist ⇒ 商品应在分销侧登记，平台侧再直登
  // 会让两套主数据打架。判据与 NAV `hideWhenMerchantHasDist` 同一处（nav.js `merchantHasDistGated`），
  // 平台超管保留可见（排查用）。
  const catalogWrap = $("mall-catalog-wrap");
  const hideCatalog = !!(catalogWrap && merchantHasDistGated());
  if (catalogWrap) catalogWrap.classList.toggle("hidden", hideCatalog);
  // 店铺资料字段已迁至概览「商家基本资料」，其加载改由 refreshAll 承担（见 loadMallShopForOverview）
  if (mall) {
    if (!hideCatalog) loadMallCatalog();
    loadMallStorePanel();
    loadMallShare();
  }
  if (plug) loadPluginSettings();
}

export function renderCredentialsPanel() {
  const me = state.me;
  const lines = [];
  if (me) {
    lines.push(`merchant_id: ${me.merchant_id}`);
    lines.push(`lifecycle: ${me.lifecycle_status}`);
    lines.push(`has_live_key: ${me.has_live_key ? "是" : "否"}`);
  }
  lines.push(`登录方式: ${authMode() === "portal" ? "扫码门户会话" : "Api-Key"}`);
  lines.push(`当前 API 环境: ${localStorage.getItem(LS.env) || "sandbox"}`);
  $("cred-summary").innerHTML = lines.join("<br>");
}

/* 个人中心：当前登录用户 + 所属主体 + 角色权限（数据均来自 state.me / McAuth）。
 *
 * 版式对齐分销管理台的「个人中心」（dist_svc/static/admin/js/pages/account.js）：一主题一张卡、
 * 每项「标签列 + 取值列」成行排版，标识类取值用 code 芯片、角色/权限码用 tag 药丸、空值统一「—」。
 * 纯展示、零可操作项（与源端一致）—— 本页不新增任何动作。
 */

/** 一行「标签 / 取值」；valueHtml 为已转义片段，空则渲染「—」 */
function acctRow(label, valueHtml) {
  return (
    '<div class="mcp-acct-row">' +
    `<div class="mcp-acct-label">${escHtml(label)}</div>` +
    `<div class="mcp-acct-val">${valueHtml || "—"}</div>` +
    "</div>"
  );
}

/** code 芯片；空值渲染「—」 */
function acctCode(text) {
  return text ? `<code class="mcp-acct-code">${escHtml(text)}</code>` : "—";
}

/** tag 药丸组；空则给一句兜底说明 */
function acctTags(items, emptyText) {
  const pills = (items || []).filter(Boolean)
    .map((t) => `<span class="mcp-acct-tag">${escHtml(t)}</span>`)
    .join("");
  return `<div class="mcp-acct-tags">${
    pills || `<span class="mcp-acct-empty">${escHtml(emptyText || "—")}</span>`
  }</div>`;
}

export function renderAccountPanel() {
  const me = state.me || {};
  const env = localStorage.getItem(LS.env) || "sandbox";
  const keyMode = authMode() !== "portal";
  const mode = keyMode ? "接口密钥" : "扫码门户会话";
  const personId =
    me.console_person_id ||
    localStorage.getItem(LS.personId) ||
    "";
  const personName = me.console_person_display_name || "";
  const personPhone = me.console_person_phone || "";

  /* ── 一、当前登录用户 ── */
  const userBox = $("account-user");
  if (userBox) {
    userBox.innerHTML = [
      acctRow(
        "用户账号",
        personId
          ? acctCode(personId) +
              (personName ? `（${escHtml(personName)}）` : "（未设显示名）")
          : "—（未关联 psn_…，请重新扫码登录或使用用户 ID 登录）"
      ),
      acctRow("手机号", personPhone ? escHtml(personPhone) : "—（未绑定）"),
      acctRow("登录方式", escHtml(mode)),
      acctRow("当前 API 环境", acctCode(env)),
      acctRow(
        "生效凭证",
        keyMode
          ? escHtml(
              env === "live" && localStorage.getItem(LS.liveKey)
                ? "Live Key"
                : "Sandbox Key"
            ) + "（脱敏不展示）"
          : "Bearer portal token（脱敏不展示）"
      ),
    ].join("");
  }

  /* ── 二、所属主体 ──
   * 平台控制台的「主体」就是当前商家本身（没有 HQ 租户那一层），字段沿用原样、不改语义。 */
  const entBox = $("account-entity");
  if (entBox) {
    entBox.innerHTML = [
      acctRow("主体 merchant_id", acctCode(me.merchant_id)),
      acctRow("店铺", me.shop_name ? escHtml(me.shop_name) : ""),
      acctRow("tenant_kind", acctCode(me.tenant_kind)),
      acctRow(
        "生命周期",
        escHtml(LIFECYCLE_LABELS[me.lifecycle_status] || me.lifecycle_status || "")
      ),
      acctRow("has_live_key", me.has_live_key ? "是" : "否"),
    ].join("");
  }

  /* ── 三、角色与权限 ── */
  const rolesBox = $("account-roles");
  if (rolesBox) {
    const roles = window.McAuth ? window.McAuth.roleDisplayNames() : [];
    const perms = window.McAuth ? window.McAuth.permissions() : me.permissions || [];
    // 仅平台超级管理员持有 *；商家管理员为显式本店权限列表
    const wild = perms.includes("*");
    const owner = window.McAuth ? window.McAuth.isOwner() : false;
    const scope = wild
      ? `平台超级管理员通配 —— 全部权限（${acctCode("*")}）`
      : owner
        ? `商家管理员（${acctCode("MERCHANT_OWNER")}）—— 本店业务权限（非通配）`
        : "";
    rolesBox.innerHTML = [
      acctTags(roles, "未绑定任何角色，请联系管理员分配"),
      acctRow(`权限（${wild ? "全部" : perms.length}）`, scope),
      !wild && perms.length ? acctTags(perms, "") : "",
    ].join("");
  }
}

/**
 * 概览「商家基本资料」填值。原在 `renderCapabilitiesPanel` 里调，
 * 字段搬走后若仍留在那里，概览页会一直空着——故改由 `refreshAll` 统一调。
 */
export async function loadMallShopForOverview() {
  if (((state.me && state.me.tenant_kind) || "") !== "platform_mall") return;
  await loadMallShop();
}

async function loadMallShop() {
  const { ok, data } = await api("GET", "/api/v1/fitting/mall/shop");
  if (!ok || !data) return;
  $("mall-shop-name").value = data.shop_name || "";
  $("mall-cs-contact").value = data.cs_contact || "";
  $("mall-notice").value = data.notice || "";
  $("cred-shop-code").value = data.shop_code || "";
}

function applyQrToImg(imgEl, dataUrl) {
  if (!imgEl) return;
  if (dataUrl) {
    imgEl.src = dataUrl;
    imgEl.classList.remove("hidden");
  } else {
    imgEl.removeAttribute("src");
    imgEl.classList.add("hidden");
  }
}

async function loadMallShare() {
  const tip = $("mall-share-tip");
  const img = $("mall-share-qr-img");
  if (tip) tip.textContent = "加载分享素材…";
  const { ok, data } = await api("GET", "/api/v1/fitting/mall/share");
  if (!ok || !data) {
    if (tip) tip.textContent = "分享素材加载失败";
    if (img) img.classList.add("hidden");
    return;
  }
  if ($("mall-share-path")) $("mall-share-path").value = data.share_path || "";
  if ($("mall-share-url-link")) $("mall-share-url-link").value = data.url_link || "";
  if (tip) {
    tip.textContent =
      data.tip || "请用微信首页扫一扫小程序码，或复制 URL Link 投放。";
  }
  const qr = data.wxacode_data_url || data.qr_image_data_url || "";
  applyQrToImg(img, qr);
}

async function copyTextFromInput(inputId, label) {
  const el = $(inputId);
  const text = el && el.value ? el.value.trim() : "";
  if (!text) {
    showBanner(`${label}为空`, false);
    return;
  }
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else if (el) {
      el.select();
      document.execCommand("copy");
    }
    showBanner(`已复制${label}`, true);
  } catch (_) {
    showBanner("复制失败，请手动选择", false);
  }
}

async function loadPluginSettings() {
  const { ok, data } = await api("GET", "/api/v1/fitting/plugin/settings");
  if (!ok || !data) return;
  $("plugin-origins").value = (data.allowed_origins || []).join("\n");
  $("plugin-callback-url").value = data.event_callback_url || "";
  $("cred-shop-code").value = data.shop_code || "";
}

export async function submitOnboarding() {
  const { ok, data } = await api("POST", "/api/v1/merchant/onboarding/submit");
  if (ok) {
    showBanner("已提交验收申请", true);
  } else {
    showBanner((data && data.message) || "提交失败", false);
  }
}

function renderAdapterSummary(summary) {
  const box = $("ast-summary");
  if (!box) return;
  if (!summary) {
    box.innerHTML = "";
    return;
  }
  const chips = [
    ["total", "合计"],
    ["succeeded", "成功"],
    ["running", "进行中"],
    ["failed", "失败"],
  ];
  box.innerHTML = chips
    .map(([k, label]) => {
      const n = summary[k] || 0;
      if (k !== "total" && !n) return "";
      const cls =
        k === "succeeded"
          ? "mcp-badge live"
          : k === "failed"
            ? "mcp-badge bad"
            : "mcp-badge";
      return `<span class="${cls}">${label}: ${n}</span>`;
    })
    .filter(Boolean)
    .join("");
}

function renderAdapterTaskTable(items) {
  const wrap = $("ast-task-table-wrap");
  if (!wrap) return;
  if (!items || !items.length) {
    wrap.innerHTML = "<p class='mc-desc'>暂无同步任务</p>";
    return;
  }
  const rows = items
    .map((it) => {
      const st = it.status || "";
      const rowCls =
        st === "succeeded" ? "delivered" : st === "failed" ? "failed" : "";
      const err = (it.last_error || "").slice(0, 80);
      const resource = it.resource_id || (it.result && it.result.product_id) || "—";
      const finished = (it.finished_at || it.started_at || "").slice(0, 19);
      return `<tr class="${rowCls}">
          <td><code>${it.task_id}</code></td>
          <td>${it.task_type || ""}</td>
          <td class="status">${st}</td>
          <td>${it.mode || "—"}</td>
          <td>${resource}</td>
          <td>${err || "—"}</td>
          <td>${finished}</td>
        </tr>`;
    })
    .join("");
  wrap.innerHTML = `
      <table class="mcp-delivery-table">
        <thead>
          <tr>
            <th>task_id</th><th>type</th><th>status</th><th>mode</th>
            <th>resource</th><th>error</th><th>时间</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
}

export async function loadAdapterSyncTasks() {
  const statusEl = $("ast-filter-status");
  const typeEl = $("ast-filter-type");
  const qs = new URLSearchParams({ limit: "50" });
  if (statusEl && statusEl.value) qs.set("status", statusEl.value);
  if (typeEl && typeEl.value) qs.set("task_type", typeEl.value);
  const { ok, data } = await api(
    "GET",
    `/api/v1/fitting/adapter/sync-tasks?${qs.toString()}`
  );
  if (!ok || !data) return;
  renderAdapterSummary(data.summary);
  renderAdapterTaskTable(data.items);
}

function renderEventSummary(summary) {
  const box = $("evt-summary");
  if (!summary) {
    box.innerHTML = "";
    return;
  }
  const chips = [
    ["total", "合计"],
    ["delivered", "成功"],
    ["pending", "待投"],
    ["failed", "失败"],
    ["skipped_no_route", "无路由"],
  ];
  box.innerHTML = chips
    .map(([k, label]) => {
      const n = summary[k] || 0;
      if (k !== "total" && !n) return "";
      const cls =
        k === "delivered"
          ? "mcp-badge live"
          : k === "failed"
            ? "mcp-badge bad"
            : "mcp-badge";
      return `<span class="${cls}">${label}: ${n}</span>`;
    })
    .filter(Boolean)
    .join("");
}

function renderEventLogTable(items) {
  const wrap = $("evt-log-table-wrap");
  if (!items || !items.length) {
    wrap.innerHTML = "<p class='mc-desc'>暂无投递记录</p>";
    return;
  }
  const rows = items
    .map((it) => {
      const st = it.status || "";
      const rowCls =
        st === "delivered" ? "delivered" : st === "failed" ? "failed" : "";
      const canRetry = st !== "delivered";
      return `<tr class="${rowCls}">
          <td><code>${it.event_id}</code></td>
          <td>${it.event_type || ""}</td>
          <td class="status">${st}</td>
          <td>${it.attempts ?? 0}</td>
          <td>${it.last_http_status ?? "—"}</td>
          <td>${(it.last_error || "").slice(0, 80)}</td>
          <td>${(it.delivered_at || it.created_at || "").slice(0, 19)}</td>
          <td class="mcp-delivery-actions">${canRetry ? `<button type="button" data-retry="${it.event_id}">重试</button>` : "—"}</td>
        </tr>`;
    })
    .join("");
  wrap.innerHTML = `
      <table class="mcp-delivery-table">
        <thead>
          <tr>
            <th>event_id</th><th>type</th><th>status</th><th>attempts</th>
            <th>http</th><th>error</th><th>时间</th><th></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
  wrap.querySelectorAll("[data-retry]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const eid = btn.getAttribute("data-retry");
      const { ok } = await api(
        "POST",
        `/api/v1/fitting/plugin/events/${encodeURIComponent(eid)}/retry`
      );
      if (ok) {
        showBanner(`已重试 ${eid}`, true);
        await loadEventLog();
      }
    });
  });
}

export async function loadEventLog() {
  await loadAdapterSyncTasks();
  const status = $("evt-filter-status").value;
  const eventType = $("evt-filter-type").value;
  const qs = new URLSearchParams({ limit: "50" });
  if (status) qs.set("status", status);
  if (eventType) qs.set("event_type", eventType);
  const { ok, data } = await api(
    "GET",
    `/api/v1/fitting/plugin/event-log?${qs.toString()}`
  );
  if (!ok) return;
  $("sync-preview").textContent = pretty(data);
  renderEventSummary(data.summary);
  renderEventLogTable(data.items);
}

export function onRouteActivate(route) {
if (route === "cap-open") renderCapabilityOpenPanel();
if (route === "capabilities") renderCapabilitiesPanel();
if (route === "credentials") renderCredentialsPanel();
if (route === "account") renderAccountPanel();
if (route === "sync") loadEventLog();
if (route === "onboarding") {
  renderOnboardingStatus(state.me, state.onboarding);
  renderChecklist(state.onboarding);
}
if (route === "brand") loadBrandPanel();
if (route === "bminiapp") loadBminiappPanel();
if (route === "stores") loadOwnStoresPanel();
  if (route === "team") loadTeamPanel();
if (route === "categories") loadCategoriesPanel();
if (route === "channel-reviews") loadChannelReviewsPanel();
if (route === "mall-orders") loadMallOrdersPanel();
if (route === "mall-reconcile") loadMallReconcilePanel();
if (route === "actors") ensureActorsEmbed();
}

export function initPanelHandlers(refreshAll) {
bindClick("btn-refresh", () => refreshAll());
bindClick("btn-onboarding-submit", () => submitOnboarding().then(() => refreshAll()));
bindClick("btn-submit-from-overview", () => submitOnboarding().then(() => refreshAll()));

updateAdminOpsUx();

bindClick("btn-rotate-sandbox", async () => {
  const { ok, data } = await api("POST", "/api/v1/merchant/keys/rotate", { body: { env: "sandbox" } });
  if (ok && data && data.api_key) {
    $("merchant-key").value = data.api_key;
    persistAuth();
    setAuthSession({ apiKey: data.api_key, mode: "api_key" });
    showBanner(`新 Sandbox Key：${data.api_key}`, true);
  }
});

bindClick("btn-rotate-live", async () => {
  const { ok, data } = await api("POST", "/api/v1/merchant/keys/rotate", { body: { env: "live" } });
  if (ok && data && data.api_key) {
    $("live-key").value = data.api_key;
    persistAuth();
    showBanner(`新 Live Key：${data.api_key}`, true);
  }
});

bindClick("btn-mall-shop-save", async () => {
  const body = {
    shop_name: $("mall-shop-name").value.trim() || null,
    cs_contact: $("mall-cs-contact").value.trim() || null,
    notice: $("mall-notice").value.trim() || null,
  };
  const { ok } = await api("PUT", "/api/v1/fitting/mall/shop", { body });
  if (ok) {
    showBanner("店铺资料已保存", true);
    await refreshAll();
  }
});

initMallCatalogHandlers();
initTeamHandlers();
initMallStoreHandlers();
initDistOpenHandlers(refreshAll);
initBrandHandlers();
initBminiappHandlers();
initOwnStoresHandlers();
initCategoriesHandlers();
initChannelReviewsHandlers();
initMallOrdersHandlers();
initMallReconcileHandlers();

bindClick("btn-mall-share-refresh", () => loadMallShare());
bindClick("btn-mall-share-copy-path", () => copyTextFromInput("mall-share-path", "小程序路径"));
bindClick("btn-mall-share-copy-link", () => copyTextFromInput("mall-share-url-link", "URL Link"));

bindClick("btn-plugin-settings-save", async () => {
  const origins = $("plugin-origins").value.split("\n").map((s) => s.trim()).filter(Boolean);
  const body = {
    channel: "PLUGIN",
    allowed_origins: origins,
    event_callback_url: $("plugin-callback-url").value.trim() || null,
  };
  const key = $("plugin-callback-key").value.trim();
  if (key) body.event_callback_key = key;
  const { ok } = await api("PUT", "/api/v1/fitting/plugin/settings", { body });
  if (ok) {
    showBanner("插件设置已保存", true);
    $("plugin-callback-key").value = "";
    await refreshAll();
  }
});

const btnPluginSmoke = $("btn-plugin-callback-smoke");
if (btnPluginSmoke) {
  btnPluginSmoke.addEventListener("click", async () => {
    const out = $("plugin-smoke-result");
    if (out) out.textContent = "运行中…";
    btnPluginSmoke.disabled = true;
    const { ok, data } = await api("POST", "/api/v1/fitting/orders/callback/smoke");
    btnPluginSmoke.disabled = false;
    if (ok && data && data.ok) {
      const msg = `通过 · order=${data.order_id || "-"} · rtask=${data.recommend_task_id || "-"}`;
      if (out) out.textContent = msg;
      showBanner("订单回传冒烟已通过", true);
      await refreshAll();
    } else {
      const errMsg = (data && (data.message || (data.error && data.error.message))) || "冒烟失败";
      if (out) out.textContent = errMsg;
      showBanner(errMsg, false);
    }
  });
}

bindClick("btn-event-log", () => loadEventLog());
bindClick("btn-sync-tasks", () => loadAdapterSyncTasks());
const astStatus = $("ast-filter-status");
const astType = $("ast-filter-type");
if (astStatus) astStatus.addEventListener("change", () => loadAdapterSyncTasks());
if (astType) astType.addEventListener("change", () => loadAdapterSyncTasks());
const fs = $("evt-filter-status");
const ft = $("evt-filter-type");
if (fs) fs.addEventListener("change", () => loadEventLog());
if (ft) ft.addEventListener("change", () => loadEventLog());

bindClick("btn-quota", async () => {
  const { ok, data } = await api("GET", "/api/v1/fitting/quota");
  $("usage-preview").textContent = pretty(data);
});
}
